<script setup>
import {
  computed,
  useTemplateRef,
  ref,
  getCurrentInstance,
  nextTick,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

// Plain dataUrl on purpose: the blobs/redirect URL is permanently signed and the
// blob content is immutable, so the old t=Date.now() cache-buster only guaranteed
// a full re-download of every note on every visit. A fresh t= is still minted in
// onError below, where busting caches is the point.
const audioSrc = ref(attachment.dataUrl);

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isLoading = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);
// Slider position while the agent is dragging — playback keeps running underneath
// but must not yank the knob out of their hand; commit happens on release.
const scrubTime = ref(null);
// Seek to apply once metadata is back after an onError reload.
const pendingSeek = ref(0);
// One automatic URL-refresh per user gesture; a second failure settles the button.
const retriedAt = ref(0);

const { uid } = getCurrentInstance();

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration || 0;
  if (audioPlayer.value) {
    audioPlayer.value.playbackRate = playbackSpeed.value;
    if (pendingSeek.value) {
      audioPlayer.value.currentTime = pendingSeek.value;
      pendingSeek.value = 0;
    }
  }
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

const seekMax = computed(() =>
  Number.isFinite(duration.value) && duration.value > 0 ? duration.value : 0
);

const displayTime = computed(() =>
  scrubTime.value === null ? currentTime.value : scrubTime.value
);

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || !Number.isFinite(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  // While a reload-seek is armed the element briefly reads 0 — hold the real
  // spot; pendingSeek is zeroed the moment onLoadedMetadata applies it.
  if (pendingSeek.value) return;
  currentTime.value = audioPlayer.value?.currentTime;
};

// Live-seeking on every drag tick aborted and reopened a range request per
// millimetre — seconds of dead air per scrub on OGG (no seek index). The drag
// only moves the local knob; the real seek happens once, on release.
const onScrub = event => {
  scrubTime.value = Number(event.target.value);
};

const onScrubEnd = event => {
  const time = Number(event.target.value);
  scrubTime.value = null;
  // An explicit scrub invalidates any staged retry-seek — without this, the
  // reload lands and yanks playback back to where it was before the error.
  pendingSeek.value = 0;
  if (!audioPlayer.value) return;
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = async () => {
  const el = audioPlayer.value;
  if (!el) return;
  // The element is the source of truth, not our ref: a headset button or
  // Chrome's media pill pauses the element without telling us, and acting on a
  // stale isPlaying used to need two clicks to recover.
  if (el.paused) {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    isLoading.value = true;
    // A fresh gesture earns a fresh automatic retry in onError.
    retriedAt.value = 0;
    if (el.error && attachment.dataUrl) {
      // An errored element cannot be revived by play() alone — the spec
      // short-circuits it with NotSupportedError and never refetches. Start
      // over with a freshly-signed URL; the src patch itself restarts the
      // load algorithm once it lands.
      pendingSeek.value = currentTime.value;
      audioSrc.value = timeStampAppendedURL(attachment.dataUrl);
      await nextTick();
    }
    const fresh = audioPlayer.value;
    if (!fresh) return;
    // play() rejects when the agent pauses again or leaves the conversation
    // before playback starts — routine browser behaviour, not a failure.
    fresh.play().catch(() => {
      isLoading.value = false;
      isPlaying.value = false;
    });
  } else {
    el.pause();
  }
};

// The element's own events drive the button, so external pauses (media keys,
// Bluetooth, the pause_playing_audio emitter) can never desync the UI again.
const onPlay = () => {
  isPlaying.value = true;
};

const onPlaying = () => {
  isPlaying.value = true;
  isLoading.value = false;
  retriedAt.value = 0;
};

const onWaiting = () => {
  // Buffering mid-note: without this the frozen timer is pixel-identical to a
  // hang, and the resulting second tap silently cancels the pending play().
  isLoading.value = true;
};

const onPause = () => {
  isPlaying.value = false;
  isLoading.value = false;
};

const onError = async () => {
  // The signed disk URL behind the redirect expires ~5 minutes after first
  // load, so resuming or seeking a note the agent opened a while ago dies with
  // a fatal media error nobody used to hear about — the player just bricked
  // until the conversation was reopened. Refresh to a newly-signed URL (fresh
  // t= so no cache serves the failure back), reload, and resume in place.
  const el = audioPlayer.value;
  if (!el) return;
  if (!attachment.dataUrl) {
    // A row whose media download failed serves an empty dataUrl — nothing to
    // retry against, and new URL('') below would throw.
    isLoading.value = false;
    isPlaying.value = false;
    return;
  }
  const wanted = isLoading.value || isPlaying.value;
  if (retriedAt.value) {
    // Second failure on the same gesture: settle back to the play icon. The
    // failure steps leave el.paused === false, so re-sync it — the next click
    // must land in the play branch, where the el.error guard starts over with
    // another fresh URL.
    isLoading.value = false;
    isPlaying.value = false;
    retriedAt.value = 0;
    el.pause();
    return;
  }
  retriedAt.value = Date.now();
  pendingSeek.value = currentTime.value;
  audioSrc.value = timeStampAppendedURL(attachment.dataUrl);
  // The load must run against the PATCHED src — synchronously it would restart
  // the old, dead URL and the patch's own load-algorithm restart would then
  // abort the play() below.
  await nextTick();
  const fresh = audioPlayer.value;
  if (!fresh) return;
  fresh.load();
  if (wanted) {
    fresh.play().catch(() => {
      isLoading.value = false;
      isPlaying.value = false;
    });
  }
};

const onEnd = () => {
  // Switching conversation mid-playback unmounts us and nulls the ref, but the
  // detached <audio> is only paused at the next stable state — a voice note
  // finishing inside that window still fires "ended" and the reset crashed.
  if (!audioPlayer.value) return;
  isPlaying.value = false;
  currentTime.value = 0;
  // The playback speed deliberately survives: agents triaging a stack of notes
  // at 1.5x lost the setting after every single note.
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const isDownloading = ref(false);
const downloadAudio = async () => {
  // downloadFile fetches the WHOLE file before the save dialog can appear —
  // untracked, that was 10-60s of nothing on screen, stacked parallel fetches
  // on repeat clicks, and failures vanishing into an unhandled rejection.
  if (isDownloading.value) return;
  const { fileType, dataUrl, extension } = attachment;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch {
    /* the button re-enabling is the feedback; a retry is one click away */
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <audio
    ref="audioPlayer"
    :src="audioSrc"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @play="onPlay"
    @playing="onPlaying"
    @waiting="onWaiting"
    @pause="onPause"
    @error="onError"
  />
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div dir="ltr" class="flex gap-1 w-full flex-1 items-center justify-start">
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="playOrPause"
      >
        <span
          v-if="isLoading"
          class="i-lucide-loader-circle animate-spin size-5 text-n-slate-11"
        />
        <Icon
          v-else-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ formatTime(displayTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="seekMax"
          :value="displayTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="onScrub"
          @change="onScrubEnd"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center disabled:opacity-50"
        :disabled="isDownloading"
        @click="downloadAudio"
      >
        <span
          v-if="isDownloading"
          class="i-lucide-loader-circle animate-spin size-4"
        />
        <Icon v-else class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>
